import { useEffect } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { Header } from '@components/header';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconDownload } from '@components/icons/download';
import { IconInfo } from '@components/icons/info';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconLock } from '@components/icons/lock';
import { IconShare } from '@components/icons/share';
import { Spinner } from '@components/loading-spinner';
import { PageContent } from '@components/page-containers/page-container';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useClipboard } from '@hooks/use-clipboard';
import { useScopedTranslate } from '@locale/translate';
import { isIframe } from '@utils/browser';

import { useCurrentUser } from 'api/authentication/hooks';
import {
  useJourneyIntegrationEndpoints,
  useJourneyIntegrationWebhook,
} from 'api/integrations/hooks';
import { useJourneyActions } from 'api/journey-actions/hooks';
import { useJourney } from 'api/journeys/hooks';
import { JourneyTemplateType } from 'api/types';
import { convertToNodes } from 'ui/authenticated/main/pages/journeys/journey-edit';
import { routePaths } from 'ui/authenticated/navigation';
import { TriggerContext } from 'ui/journey-builder/base-builder';
import { useNodes } from 'ui/journey-builder/hooks/use-nodes';
import { JourneyTemplateBuilder } from 'ui/journey-template/template-builder';

import { useGetTemplateModal } from './get-template-modal';
import { useJourneyDetailsSidebar } from './journey-details-sidebar';

const signinRoute = '/signin';

export const JourneyTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { journeyId = '' } = useParams();
  const journeyQuery = useJourney(journeyId);
  const location = useLocation();
  const showJourneyDetailSidebar = useJourneyDetailsSidebar();
  const showGetTemplateModal = useGetTemplateModal();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const { onCopy } = useClipboard();

  useEffect(() => {
    if (location.state?.from?.pathname === signinRoute) {
      setTimeout(() => {
        showGetTemplateModal({ journeyTemplateId: journeyId });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const placeholderJourney: JourneyTemplateType = {
    shortId: '',
    description: '',
    title: t('defaultTitle'),
  };

  const journey = journeyQuery.data ?? placeholderJourney;

  if (journeyQuery.isError) {
    return <div>{t('journeyNotFound')}</div>;
  }

  if (journeyQuery.isLoading) {
    return <Spinner />;
  }

  const handleShareClick = () => {
    onCopy(window.location.href, t('linkCopied'));
  };

  const handleGetTemplateClick = () => {
    if (currentUser) {
      showGetTemplateModal({ journeyTemplateId: journeyId });
    } else {
      const pathname = generatePath(routePaths.templates, {
        journeyId,
      });
      if (isIframe) {
        window.open(pathname, '_blank');
      } else {
        navigate(signinRoute, {
          state: {
            from: {
              pathname,
            },
          },
        });
      }
    }
  };

  return (
    <PageContent noPadding>
      <Header
        breadcrumbs={
          <Stack space={2} orientation="horizontal">
            <IconFancyContainer
              icon={<IconJourneyItem size={24} color="green200" />}
              variant="green"
            />
            <Breadcrumb items={[{ label: journey.title }]} />
          </Stack>
        }
        actions={
          <Stack space={6} orientation="horizontal" align="center">
            <Stack space={2} orientation="horizontal" align="center">
              <IconLock size={16} color="purple50" />
              <Text text={t('viewMode')} color="textHelper" font="regular14" />
            </Stack>
            <Stack space={2} orientation="horizontal">
              <Button
                text={t('share')}
                iconLeft={<IconShare />}
                variant="secondary"
                onClick={handleShareClick}
              />
              <Button
                text={t('details')}
                iconLeft={<IconInfo />}
                variant="secondary"
                onClick={() => showJourneyDetailSidebar({ journeyTemplate: journey })}
              />

              <Button
                text={t('getTemplate')}
                iconLeft={<IconDownload />}
                onClick={handleGetTemplateClick}
              />
            </Stack>
          </Stack>
        }
      />
      <JourneyFlow journey={journey} />
    </PageContent>
  );
};

const JourneyFlow = ({ journey }: { journey: JourneyTemplateType }) => {
  const { setNodes } = useNodes();
  const integrationQuery = useJourneyIntegrationEndpoints(journey.shortId ?? '');
  const integrations = Array.isArray(integrationQuery.data) ? integrationQuery.data : [];
  const webhookQuery = useJourneyIntegrationWebhook(journey.shortId ?? '');
  const webhooks = webhookQuery.data ?? [];
  const enabled = Boolean(journey.shortId) && !integrationQuery.isLoading;
  useJourneyActions(journey.shortId, {
    onSuccess: (data) => {
      if (data.length > 0) {
        setNodes(
          convertToNodes({
            actions: data,
            integrations,
            webhooks,
            collapsedActions: [],
            // Shit fix for now so layout is correct, don't know why it doesn't behave the same
            // as in the normal journey builder
            triggersCount: 0.5,
            isTriggersCollapsed: false,
          }),
          true
        );
      }
    },
    enabled,
  });
  if (integrationQuery.isLoading) {
    return <Spinner />;
  }
  return (
    <TriggerContext.Provider
      value={{
        forms: [],
        webhooks: [],
        pollingConfigs: [],
        triggersCount: 0,
        journeyId: journey.shortId,
      }}
    >
      <JourneyTemplateBuilder journeyId={journey.shortId} isTemplate />
    </TriggerContext.Provider>
  );
};

const locale = {
  en: {
    share: 'Share',
    journeyNotFound: 'Journey not found',
    defaultTitle: 'Loading',
    noDescription: 'No description',
    getTemplate: 'Get template',
    details: 'Details',
    linkCopied: 'Link copied to clipboard',
    loginToGetTemplate: 'Please sign in to get the template',
    viewMode: 'View mode',
  },
};
